import { useEffect, useState } from 'react';
import PageHero from '@/components/PageHero';
import { ErrorBox, Loading } from '@/components/Status';
import { MarketPriceItem, getMarketPrice } from '@/api';
import '../styles/market.css';

// 静态历史趋势示意数据（近 7 日，品种均价）
const TREND: { day: string; value: number }[] = [
  { day: '07-27', value: 24.8 },
  { day: '07-28', value: 25.1 },
  { day: '07-29', value: 24.9 },
  { day: '07-30', value: 25.4 },
  { day: '07-31', value: 25.2 },
  { day: '08-01', value: 25.8 },
  { day: '08-02', value: 26.1 },
];

function TrendChart() {
  const W = 560;
  const H = 190;
  const PAD = 26;
  const values = TREND.map((t) => t.value);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;

  const points = TREND.map((t, i) => {
    const x = PAD + (i * (W - PAD * 2)) / (TREND.length - 1);
    const y = H - PAD - ((t.value - min) / range) * (H - PAD * 2);
    return { ...t, x, y };
  });

  const linePath = points.map((p) => `${p.x},${p.y}`).join(' ');
  const areaPath = `M ${points[0].x},${H - PAD} L ${linePath.replace(/ /g, ' L ')} L ${points[points.length - 1].x},${H - PAD} Z`;

  return (
    <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label="近 7 日品种均价趋势示意" className="trend-svg">
      <defs>
        <linearGradient id="trendFill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#0f4c5c" stopOpacity="0.28" />
          <stop offset="1" stopColor="#0f4c5c" stopOpacity="0.02" />
        </linearGradient>
      </defs>
      <path d={areaPath} fill="url(#trendFill)" />
      <polyline points={linePath} fill="none" stroke="#0f4c5c" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
      {points.map((p) => (
        <g key={p.day}>
          <circle cx={p.x} cy={p.y} r="4.5" fill="#fff" stroke="#0f4c5c" strokeWidth="2.5" />
          <text x={p.x} y={H - 4} textAnchor="middle" fontSize="11.5" fill="#5d6d72">
            {p.day}
          </text>
          <text x={p.x} y={p.y - 12} textAnchor="middle" fontSize="11.5" fontWeight="700" fill="#0a3a46">
            {p.value.toFixed(1)}
          </text>
        </g>
      ))}
    </svg>
  );
}

export default function Market() {
  const [prices, setPrices] = useState<MarketPriceItem[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    document.title = '市场行情 | 云牛集·山东牛产业智慧平台';
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    getMarketPrice()
      .then((d) => {
        if (!cancelled) setPrices(d);
      })
      .catch((e) => {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : '行情加载失败，请稍后重试');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [reloadKey]);

  const sorted = prices ? [...prices].sort((a, b) => b.price - a.price) : [];
  const maxPrice = sorted.length > 0 ? sorted[0].price : 0;
  const highest = sorted[0];
  const lowest = sorted[sorted.length - 1];
  const latestDate = prices && prices.length > 0 ? prices[0].date : '';

  return (
    <>
      <PageHero
        title="市场行情"
        desc="展示平台内已挂牌肉牛品种的参考价格。下方价格表来自后端行情接口；历史趋势为示意，不代表实际成交价。"
      />

      <section className="section">
        <div className="container">
          {loading && <Loading label="正在加载市场行情…" />}

          {!loading && error && (
            <ErrorBox message={error} onRetry={() => setReloadKey((k) => k + 1)} />
          )}

          {!loading && prices && (
            <>
              <div className="market-summary">
                <div className="summary-card">
                  <span>最高价品种</span>
                  <strong>{highest?.breed ?? '—'}</strong>
                  <em>{highest?.price ?? '—'} 元/kg</em>
                </div>
                <div className="summary-card">
                  <span>最低价品种</span>
                  <strong>{lowest?.breed ?? '—'}</strong>
                  <em>{lowest?.price ?? '—'} 元/kg</em>
                </div>
                <div className="summary-card">
                  <span>行情日期</span>
                  <strong>{latestDate || '—'}</strong>
                  <em>共 {prices.length} 个品种</em>
                </div>
              </div>

              <div className="market-grid">
                <section className="market-panel">
                  <h3>品种价格一览</h3>
                  <div className="price-table-wrap">
                    <table className="price-table">
                      <thead>
                        <tr>
                          <th>品种</th>
                          <th>价格</th>
                          <th>单位</th>
                          <th>日期</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sorted.map((item) => (
                          <tr key={item.breed}>
                            <td className="cell-breed">{item.breed}</td>
                            <td className="cell-price">{item.price.toFixed(1)}</td>
                            <td>{item.unit}</td>
                            <td className="cell-date">{item.date}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </section>

                <section className="market-panel">
                  <h3>价格对比</h3>
                  <p className="panel-note">按价格从高到低（条形长度表示相对价格）</p>
                  <ul className="bar-list">
                    {sorted.map((item) => (
                      <li key={item.breed} className="bar-row">
                        <span className="bar-name">{item.breed}</span>
                        <span className="bar-track">
                          <span
                            className="bar-fill"
                            style={{ width: `${Math.max(4, (item.price / maxPrice) * 100)}%` }}
                          />
                        </span>
                        <strong className="bar-value">{item.price.toFixed(1)}</strong>
                      </li>
                    ))}
                  </ul>
                </section>
              </div>

              <section className="trend-panel">
                <div className="trend-head">
                  <div>
                    <h3>近 7 日价格趋势</h3>
                    <p className="panel-note">静态示意数据，正式版本将接入行情历史接口</p>
                  </div>
                  <span className="trend-badge">示意</span>
                </div>
                <TrendChart />
              </section>

              <p className="market-disclaimer">
                以上价格为平台采集的公开参考价，实际成交以双方协商为准。
              </p>
            </>
          )}
        </div>
      </section>
    </>
  );
}
